import { useEffect, useMemo, useState } from 'react'
import { useCoinAllQuery } from '@queries/common/coinAllQuery.js'

const INITIAL_RETRY_STATE = {
  missingKey: '',
  status: 'idle'
}

const inFlightRetries = new Map()

const retryCoinMetaOnce = (missingKey, refetch) => {
  const inFlight = inFlightRetries.get(missingKey)

  if (inFlight) {
    return inFlight
  }

  const retry = refetch({ cancelRefetch: false }).finally(() => {
    inFlightRetries.delete(missingKey)
  })

  inFlightRetries.set(missingKey, retry)

  return retry
}

export const useCoinMeta = ({ assetIds = [], enabled = true } = {}) => {
  const [retryState, setRetryState] = useState(INITIAL_RETRY_STATE)

  const { data: coins, isLoading, isError, refetch } = useCoinAllQuery()

  const coinMetaMap = useMemo(() => {
    return Object.fromEntries((coins ?? []).map((coin) => [String(coin.assetId), coin]))
  }, [coins])

  const normalizedAssetIds = useMemo(() => {
    return [...new Set(assetIds.map(String))].sort()
  }, [assetIds])

  const missingAssetIds = useMemo(() => {
    return normalizedAssetIds.filter((assetId) => !coinMetaMap[assetId])
  }, [normalizedAssetIds, coinMetaMap])

  const missingKey = missingAssetIds.join(',')

  useEffect(() => {
    if (!enabled || isLoading || isError) {
      return
    }

    if (!missingKey) {
      setRetryState((currentState) => {
        if (currentState.missingKey === '' && currentState.status === 'idle') {
          return currentState
        }

        return INITIAL_RETRY_STATE
      })

      return
    }

    if (retryState.missingKey === missingKey) {
      return
    }

    setRetryState({
      missingKey,
      status: 'loading'
    })

    const refetchMissingMeta = async () => {
      const result = await retryCoinMetaOnce(missingKey, refetch)

      setRetryState((currentState) => {
        if (currentState.missingKey !== missingKey) {
          return currentState
        }

        return {
          missingKey,
          status: result.isError ? 'error' : 'done'
        }
      })
    }

    refetchMissingMeta()
  }, [enabled, isError, isLoading, missingKey, refetch, retryState.missingKey])

  const hasCompletedRetry =
    retryState.missingKey === missingKey && ['done', 'error'].includes(retryState.status)

  return {
    coinMetaMap,
    missingAssetIds,
    hasCompletedRetry,
    isLoading,
    isError,
    refetch
  }
}
